/* eslint-disable react/prop-types */

import classNames from 'classnames';
import { useState, useEffect, useRef } from 'react';
import imageIcon from 'assets/img/icons/image-icon.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AttachmentPreview from 'components/common/AttachmentPreview';
import { convertFileToAttachment } from 'helpers/utils';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import styled from '@emotion/styled';

const HoverWrapper = styled('div')`
  position: relative;
  & button {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }
  &::after {
    content: '';
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    top: 0;
    left: 0;
    border-radius: 0.375rem;
    z-index: 1;
  }
  &:hover button {
    display: block;
  }
  &:hover::after {
    opacity: 1;
  }
`;

const ImageInput = ({
  file,
  setFile,
  message,
  touched,
  error,
  handleTouched,
  controlId
}) => {
  const [previews, setPreviews] = useState([]);
  const inputFileRef = useRef(null);

  // Handle file drop
  const handleFileDrop = event => {
    event.preventDefault();
    if (!touched) {
      handleTouched();
    }

    const files = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    if (files.length > 1) {
      toast.error('Only one document allowed');
      return;
    }

    const file = files[0];
    setFile(file);
    setPreviews([convertFileToAttachment(file)]);
  };

  const handleRemoveFile = () => {
    setFile('');
    setPreviews([]);
  };

  useEffect(() => {
    if (file && typeof file === 'string') {
      if (file.includes('https://') && previews?.length < 1) {
        setPreviews([
          { preview: file, name: controlId, format: 'jpeg', size: '0 KB' }
        ]);
      }
    }
  }, [file]);

  // Trigger the file input on click
  const handleDropzoneClick = () => {
    inputFileRef.current.click();
  };

  return (
    <>
      {file ? (
        previews.map((file, index) => (
          <HoverWrapper
            key={index}
            className={classNames(
              'border rounded d-flex align-items-center justify-content-between py-2 ps-2 pb-2 px-1'
            )}
            style={{
              paddingTop: '.65rem !important',
              paddingBottom: '.65rem !important'
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <AttachmentPreview attachment={file} />

              <button
                className="btn p-0"
                onClick={() => handleRemoveFile(index)}
              >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  className="fs-0 text-danger"
                />
              </button>
            </div>
          </HoverWrapper>
        ))
      ) : (
        <>
          <div
            className={classNames('dropzone dropzone-sm p-1 pb-2', {
              'border border-dashed border-danger': error && touched
            })}
            onDrop={handleFileDrop}
            onDragOver={e => e.preventDefault()} // Prevent default to allow drop
            onClick={handleDropzoneClick} // Trigger file input on click
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleFileDrop} // Handles file selection
              name={controlId}
              ref={inputFileRef} // Use the ref to trigger input
              className="d-none" // Hide the actual input
            />

            <div className="text-600 fs-9">
              {message}
              <br />
              <img
                className="mt-1"
                src={imageIcon}
                width={classNames('24')}
                alt=""
              />
            </div>
          </div>
          {touched && error ? (
            <small className="text-danger fs-11 ms-1">{error}</small>
          ) : null}
        </>
      )}
    </>
  );
};

export default ImageInput;
